import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Heart } from "lucide-react";

const AnimatedBlock = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const AboutSection = () => {
  return (
    <section
      id="chi-sono"
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#F5F0EB]"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <AnimatedBlock>
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(68,47,35,0.08)]">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_inner-light-path-2/artifacts/jh9h20r3_AlessiaStella-300.webp"
                  alt="Alessia Stella - Coach dell'Anima, fondatrice di Light Workers House"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                  data-testid="about-image"
                />
              </div>
              {/* Decorative accent */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#E1C340]/10 rounded-full blur-2xl" />
            </div>
          </AnimatedBlock>

          {/* Text */}
          <div>
            <AnimatedBlock delay={0.1}>
              <p className="text-sm uppercase tracking-[0.2em] text-[#AC8E60] font-body font-semibold mb-4">
                Chi sono
              </p>
            </AnimatedBlock>

            <AnimatedBlock delay={0.2}>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#442F23] mb-2">
                Mi presento a te,
              </h2>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#AC8E60] mb-4">
                sono Alessia Stella
              </h2>
            </AnimatedBlock>

            <AnimatedBlock delay={0.3}>
              <p className="font-accent text-xl text-[#AC8E60]/80 mb-8">
                Coach dell'Anima, Guerriera di Luce
              </p>
            </AnimatedBlock>

            <AnimatedBlock delay={0.4}>
              <p className="font-body text-base text-[#442F23]/75 leading-relaxed mb-5">
                Da sempre cammino tra luce e ombra per accompagnare gli altri a realizzare il loro scopo di vita. Guida nel viaggio dell'espansione della propria Luce attraverso l'integrazione delle ombre, la mia missione &egrave; risvegliare la luce interiore di anime coraggiose, liberare l'essenza luminosa, sciogliere blocchi profondi e permettere loro di vivere con autenticit&agrave;, nel pieno del potere personale.
              </p>
            </AnimatedBlock>

            <AnimatedBlock delay={0.5}>
              <p className="font-body text-base text-[#442F23]/75 leading-relaxed mb-5">
                La mia passione per l'essere umano nasce da lontano, coltivata con Laurea in Scienze della Formazione degli Adulti e consolidata da pi&ugrave; di 15 anni nelle risorse umane in Italia e all'estero. Ma &egrave; stato un incidente autostradale a mostrarmi la strada: miracolosamente illesa, ho compreso il mio scopo e ho avviato la mia attivit&agrave; di coaching dell'Anima.
              </p>
            </AnimatedBlock>

            <AnimatedBlock delay={0.6}>
              <p className="font-body text-base text-[#442F23]/75 leading-relaxed mb-5">
                Oggi, attraverso percorsi che intrecciano energia, corpo, sistema nervoso, mente e anima, accompagno chi desidera liberarsi da blocchi e schemi limitanti e liberare pienamente la propria essenza di Luce e portarla nel mondo.
              </p>
            </AnimatedBlock>

            <AnimatedBlock delay={0.7}>
              <p className="font-body text-sm text-[#442F23]/60 leading-relaxed mb-8 italic">
                Ogni diploma, ogni formazione &ndash; dal Master in Coaching dell'Anima alla danza somatica e terapeutica, dal diploma di Accompagnatore Spirituale al Fine Vita all'operatrice medianico-energetica &ndash; &egrave; uno strumento per creare trasformazioni reali nei miei percorsi evolutivi.
              </p>
            </AnimatedBlock>

            <AnimatedBlock delay={0.9}>
              <p className="font-body text-base text-[#AC8E60] leading-relaxed mb-6 font-medium">
                Se sei pronta/o a guardare dentro di te, a sciogliere i tuoi blocchi e svelare i tuoi autosabotaggi, a riscoprire la potenza della tua luce e a realizzare la tua missione di vita, io sono qui per guidarti.
              </p>
              <button
                onClick={() => document.getElementById("pricing-section")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-primary text-sm mt-2"
                data-testid="about-cta"
              >
                Voglio Iscrivermi
              </button>
            </AnimatedBlock>

            <AnimatedBlock delay={0.9}>
              <div className="flex items-center gap-3 text-[#AC8E60]">
                <Heart size={18} className="fill-[#E1C340] text-[#E1C340]" />
                <span className="font-body text-sm font-medium">
                  info@stellasoulcoach.com
                </span>
              </div>
            </AnimatedBlock>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
